import { ArrowLeft, ExternalLink } from 'react-feather';

import { useAuthToken, useMobile } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { TranslatedLink } from '@/utils/translatedComponents';
import { createNavigationHandler } from '@/utils/navigation';

import useIsIframe from '@/hooks/useIsIframe';
import { getDiscoveryAppUrlWithJwt } from '@/utils/discovery';
import { openContentHubInNewTab } from '@/utils/contentHub';
import { message } from 'antd/lib';
import styles from './NavigationLink.module.css';
import { NAVIGATION_LINKS_PROPS } from './NavigationLinks';

interface LinkComponentProps {
  onClick?: (input: TypeAny) => TypeAny;
  isMobile: boolean;
  isBack?: boolean;
  link: TypeAny;
  text: string;
}

function LinkComponent({ isMobile, onClick, isBack, link, text }: LinkComponentProps) {
  const { href, icon } = link;

  return (
    <TranslatedLink
      onClick={createNavigationHandler(
        href,
        undefined, // Use window.location.pathname as default
        isMobile && onClick ? onClick : undefined
      )}
      href={href}
      isBlock
      inherit
    >
      <span className={styles.navigationLinkContent} data-has-icon={!!icon}>
        {(isBack || icon) && (
          <span
            className={styles.navigationLinkContentIcon}
            data-is-logout={link.id === 'logout'}
            data-is-back={isBack}
          >
            {isBack ? <ArrowLeft {...NAVIGATION_LINKS_PROPS(isMobile)} /> : icon}
          </span>
        )}

        <span className={styles.navigationLinkContentText}>{text}</span>
      </span>
    </TranslatedLink>
  );
}

interface NavigationLinkProps {
  user: Api.UserObject;
  onClick?: (input: TypeAny) => TypeAny;
  currentLink?: string;
  // todos: Api.Influencer.CollaborationStatus[];
  link: TypeAny & {
    subnav?: TypeAny[];
  };
}

export function NavigationLink({ currentLink = '', onClick, link, user }: NavigationLinkProps) {
  const isMobile = useMobile();
  const token = useAuthToken();
  const { t } = useTranslation();
  const isIframe = useIsIframe();

  const linkProps = {
    isMobile,
    onClick
  };

  const openContentHub = () => {
    if (!user.permissions?.show_menu_content_hub || !user.team?.content_hub_url) {
      message.error(t('navigationLink.contentHubUnavailable'));
      return;
    }
    openContentHubInNewTab(user.team.content_hub_url);
    if (isMobile && onClick) {
      onClick(null as TypeAny);
    }
  };

  const openDiscovery = () => {
    if (!token) {
      message.error(t('navigationLink.discoverySignInRequired'));
      return;
    }
    const url = getDiscoveryAppUrlWithJwt(token);
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    w?.focus();
    if (isMobile && onClick) {
      onClick(null as TypeAny);
    }
  };

  if (link.id === 'discovery') {
    const { icon, id } = link;
    return (
      <li
        data-is-active={false}
        key={`navigation-link-${link.id}`}
        className={styles.navigationLink}
      >
        <button
          type="button"
          className={styles.navigationLinkDiscoveryButton}
          onClick={openDiscovery}
          aria-label={t('navigationLink.discoveryButtonAriaLabel')}
        >
          <span
            className={`${styles.navigationLinkContent} ${styles.navigationLinkContentWithTrailingIcon}`}
            data-has-icon={!!icon}
          >
            {icon && <span className={styles.navigationLinkContentIcon}>{icon}</span>}
            <span className={styles.navigationLinkContentText}>{t(`navigationLink.${id}`)}</span>
            <ExternalLink size={14} className={styles.navigationLinkExternalIcon} aria-hidden />
          </span>
        </button>
      </li>
    );
  }

  if (link.id === 'contentHub') {
    const { icon, id } = link;
    return (
      <li
        data-is-active={false}
        key={`navigation-link-${link.id}`}
        className={styles.navigationLink}
      >
        <button
          type="button"
          className={styles.navigationLinkDiscoveryButton}
          onClick={openContentHub}
          aria-label={t('navigationLink.contentHubButtonAriaLabel')}
        >
          <span
            className={`${styles.navigationLinkContent} ${styles.navigationLinkContentWithTrailingIcon}`}
            data-has-icon={!!icon}
          >
            {icon && <span className={styles.navigationLinkContentIcon}>{icon}</span>}
            <span className={styles.navigationLinkContentText}>{t(`navigationLink.${id}`)}</span>
            <ExternalLink size={14} className={styles.navigationLinkExternalIcon} aria-hidden />
          </span>
        </button>
      </li>
    );
  }

  return (
    <li
      data-is-active={currentLink.includes(link.href)}
      key={`navigation-link-${link.id}`}
      className={styles.navigationLink}
    >
      {!isIframe && (
        <LinkComponent {...linkProps} text={t(`navigationLink.${link.id}`)} link={link} />
      )}

      {!!link.subnav?.length && currentLink.includes(link.href) && (
        <ul className={styles.navigationLinkSubnav}>
          <li className={styles.navigationLinkSubnavBack}>
            <LinkComponent
              {...linkProps}
              text={t(`navigationLink.${link.id}`)}
              link={link}
              isBack
            />
          </li>

          {link.subnav.map((subLink: TypeAny) => {
            // const linkTodo =
            //   todos?.find((todo) => todo.collaboration_id === collaborationId) ||
            //   ({} as Record<string, TypeAny>);

            return (
              <li
                data-is-active={currentLink === subLink.href}
                key={`navigation-link-subnav-${subLink.id}`}
                className={styles.navigationLink}
              >
                <LinkComponent
                  {...linkProps}
                  text={t(`navigationLink.${link.id}${subLink.id}`)}
                  link={subLink}
                />
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
